import { useEffect, useState } from "react";
import { SmartImg } from "@/components/SmartImg";
import { CalendarCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ServiceIcon } from "@/components/ServiceIcon";
import { fmtPrice } from "@/lib/format";
import { slugify } from "@/lib/constants";

const ServiceCard = ({ service, onOpen, index }) => {
  const slug = slugify(service.name);
  return (
    <article className="lift reveal group flex flex-col overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-white"
             style={{ animationDelay: `${index * 90}ms` }} data-testid={`service-card-${slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F7FA]">
        {service.image_url ? (
          <div className="h-full w-full transition-transform duration-700 group-hover:scale-105">
            <SmartImg src={service.image_url} alt={service.name} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      framing={service.image_framing} className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="grid h-full place-items-center text-[#1E6FD9]"><ServiceIcon name={service.icon} className="h-12 w-12" /></div>
        )}
        <span className="absolute left-4 top-4 grid h-11 w-11 place-items-center rounded-xl bg-white/90 text-[#1E6FD9] shadow-md backdrop-blur">
          <ServiceIcon name={service.icon} />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-bold text-[#0B2545]" data-testid={`service-name-${slug}`}>{service.name}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{service.description}</p>
        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-5">
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid={`service-duration-${slug}`}>
            <Clock className="h-4 w-4 text-[#14B8A6]" /> {service.duration_min} min
          </span>
          <span className="font-display text-2xl font-extrabold text-[#0B2545]" data-testid={`service-price-${slug}`}>
            {fmtPrice(service.price)}
          </span>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={() => onOpen("info", service.id)} className="rounded-full border-slate-300 text-[#0B2545] hover:border-[#1E6FD9] hover:text-[#1E6FD9]"
                  data-testid={`service-info-button-${slug}`}>Solicitar información</Button>
          <Button onClick={() => onOpen("agendar", service.id)} className="rounded-full bg-[#1E6FD9] hover:bg-[#0B2545]"
                  data-testid={`service-agendar-button-${slug}`}>
            <CalendarCheck className="mr-1.5 h-4 w-4" /> Agendar
          </Button>
        </div>
      </div>
    </article>
  );
};

const readSlug = () => (typeof window === "undefined" ? "" : new URLSearchParams(window.location.search).get("categoria") || "");

const CategoryChips = ({ categories, active, onPick }) => (
  <div className="-mx-5 mt-10 overflow-x-auto px-5 sm:mx-0 sm:px-0" data-testid="services-category-chips">
    <div className="flex w-max gap-2 pb-1 sm:w-auto sm:flex-wrap" role="tablist" aria-label="Categorías de servicios">
      {[{ slug: "", name: "Todos" }, ...categories].map((c) => (
        <button key={c.slug || "all"} type="button" role="tab" aria-selected={active === c.slug} onClick={() => onPick(c.slug)}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${active === c.slug
                  ? "border-[#0B2545] bg-[#0B2545] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#1E6FD9] hover:text-[#1E6FD9]"}`}
                data-testid={`services-chip-${c.slug || "todos"}`}>{c.name}</button>
      ))}
    </div>
  </div>
);

export const ServicesSection = ({ services, categories = [], onOpen }) => {
  const [slug, setSlug] = useState(readSlug);
  const current = categories.find((c) => c.slug === slug || c.slug_aliases?.includes(slug));
  const active = current ? current.slug : "";
  const pick = (s) => {
    setSlug(s);
    const url = new URL(window.location.href);
    if (s) url.searchParams.set("categoria", s); else url.searchParams.delete("categoria");
    window.history.replaceState(null, "", url);
  };
  useEffect(() => {
    if (readSlug() && categories.some((c) => c.slug === readSlug() || c.slug_aliases?.includes(readSlug()))) document.getElementById("servicios")?.scrollIntoView();
  }, [categories]);
  const shown = current ? services.filter((s) => s.category_id === current.id) : services;
  return (
  <section id="servicios" className="bg-white py-24 lg:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#14B8A6]">Servicios</p>
          <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold tracking-tight text-[#0B2545] sm:text-4xl">
            Diagnóstico preciso, atención cercana
          </h2>
        </div>
        <p className="max-w-sm text-base text-slate-500 md:text-lg">Precios claros y tiempos definidos para cada estudio.</p>
      </div>
      {categories.length > 0 && <CategoryChips categories={categories} active={active} onPick={pick} />}
      {current?.description && <p className="mt-4 max-w-2xl text-sm text-slate-500" data-testid="services-category-description">{current.description}</p>}
      <div key={active || "all"} className={`${categories.length ? "mt-8" : "mt-14"} grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3`} data-testid="services-grid">
        {shown.map((s, i) => <ServiceCard key={s.id} service={s} onOpen={onOpen} index={i} />)}
      </div>
    </div>
  </section>
  );
};
